<template>
  <div class="p-reviews__item">
    <div class="p-reviews__header">
      <p class="p-reviews__name">{{ review.attributes.store_name }}</p>
      <div v-if="showApprovalFlowData" class="approvalFlowData">
        <KkStatusLabel
          :text="approvalText"
          :status="approvalStatus"
        />
        <p>{{ lastEditedAtTime }}</p>
        <p>{{ approvalLatestStatusMessage }}</p>
      </div>
      <div v-else-if="review.attributes.reply_status" class="approvalFlowData">
        <KkStatusLabel
          :text="replyStatusText"
          :status="replyStatus"
        />
      </div>
    </div>
    <div class="p-reviews__summary">
      <div class="p-reviews__site">
        <div class="p-reviews__site__icon">
          <KkSiteIcon :name="review.attributes.site_type" />
        </div>
        <div
          class="p-reviews__site__name"
          data-test="review_item_site_name"
          v-text="review.attributes.site_name"
        />
      </div>
      <div class="p-reviews__reviewRating">
        <div class="c-rating">
          <div class="c-rating__number">
            <template v-if="review.attributes.rating">
              {{ review.attributes.rating }}
            </template>
            <template v-else>
              -
            </template>
          </div>
          <Star
            v-show="review.attributes.rating"
            :rating="review.attributes.rating"
          />
        </div>
      </div>
      <div
        class="p-reviews__postedDate"
        v-text="review.attributes.posted_at"
      />
      <div
        v-show="review.attributes.posted_at !== review.attributes.reviewer_edited_at"
        class="p-reviews__reviewerEditedDate"
        v-text="$t('pages.CompaniesReviews.updatedOn', { date: review.attributes.reviewer_edited_at })"
      />
      <div
        class="p-reviews__reviewer"
        v-text="review.attributes.reviewer_name"
      />
    </div>
    <div class="p-reviews__content">
      <div
        class="p-reviews__title"
        v-text="review.attributes.title"
      />
      <!-- 本文 翻訳付き -->
      <div v-if="review.attributes.translation" class="translation">
        <div class="p-reviews__comment">
          <KkTag
            :label="$t('pages.CompaniesCompetitorsReviews.translation')"
            icon-name="ja"
            color="secondary"
            class="translation__tag"
          />
          <KkMore
            :text="review.attributes.translation.comment_ja"
            :keyword="searchQuery"
          />
        </div>
        <div class="p-reviews__comment translation--original">
          <KkTag
            :label="$t('pages.CompaniesCompetitorsReviews.originalText', { locale })"
            icon-name="global"
            color="gray"
            class="translation__tag"
          />
          <KkMore
            :text="review.attributes.comment"
            :keyword="searchQuery"
          />
        </div>
      </div>
      <!-- 本文 原文のみ -->
      <template v-else>
        <div class="p-reviews__comment">
          <KkMore
            :text="review.attributes.comment"
            :keyword="searchQuery"
          />
        </div>
      </template>
      <div class="p-reviews__source">
        {{ $t('labels.source') }}
        <a
          :href="review.attributes.site_url"
          target="_blank"
          v-text="review.attributes.site_name"
        />
      </div>
      <KkReviewImages
        v-if="showReviewImages"
        :images="review.attributes.photos"
        :site-type="review.attributes.site_type"
        :site-name="review.attributes.site_name"
        :store-name="review.attributes.store_name"
        :posted-at="review.attributes.posted_at"
        class="reviewImage"
      />
      <Reply
        v-if="isVisibleReply"
        :review="review"
        :initial-content="review.attributes.reply_content"
        :initial-posted-at="review.attributes.reply_posted_at"
        :initial-replyer-name="review.attributes.replyer_name"
        :can-reply="canReply"
        :approver-name="review.attributes.approver_name"
        :approval-status="review.attributes.reply_approval_status"
        :approval-notes="review.attributes.reply_approval_comments"
        :reviews-request-params-for-apply="reviewsRequestParamsForApply"
        @close-reply="closeReply"
        @data-refresh="$emit('data-refresh')"
      />
      <div class="p-reviews__memo is-edit">
        <div class="p-reviews__memo__name">メモ</div>
        <div class="p-reviews__memo__field">
          <textarea class="c-textArea" />
        </div>
        <div class="p-reviews__memo__note">
          この口コミにメモが残せます。このメモは口コミサイトには公開されません。
        </div>
        <div class="p-reviews__memo__edit">
          <span>2020年12月22日 10:04 にメモしました。</span>
          <a href="" class="js-memoEdit">"このメモを編集する</a>
        </div>
        <div class="c-button c-button--right">
          <a href="" class="c-button__item c-button__item--outline-cancel js-memoHide">キャンセル</a>
          <a href="" class="c-button__item c-button__item--primary">保存する</a>
        </div>
      </div>
    </div>
    <div
      class="p-reviews__operations"
    >
      <div
        v-if="isVisibleOperations"
        class="p-reviews__operations__reply"
        @click="openReply"
      >
        <img :src="iconReplyPath" alt="">
        {{ $t('pages.CompaniesReviews.reply') }}
      </div>
      <div v-if="review.attributes.report_page_url.length > 0" class="p-reviews__operations__report">
        <a :href="review.attributes.report_page_url" target="_blank" @click="handleReport">
          <img :src="iconReportPath" alt="">
          {{ $t('pages.CompaniesReviews.report') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import Star from '../Star.vue'
import { KkSiteIcon } from '../base'
import { KkStatusLabel, KkTag, KkMore } from '../base'
import Reply from './Reply.vue'
import iconReplyPath from '../../../../images/common/icon_reply.svg'
import iconReportPath from '../../../../images/common/icon_report.svg'
import moment from 'moment'
import { KkReviewImages } from '../../domains/review'
import { mapState, mapGetters } from 'vuex'
import { actionEvent } from 'src/biz_spa/modules/googleAnalytics'

export default {
  name: 'ReviewItem',
  components: {
    KkStatusLabel,
    Star,
    KkSiteIcon,
    KkMore,
    KkTag,
    Reply,
    KkReviewImages
  },
  props: {
    review: {
      type: Object,
      required: true,
    },
    searchQuery: {
      validator(value) {
        return typeof value === 'string' || value == null
      },
    },
    replyApprovalFlow: {
      type: Boolean,
      required: true,
    },
    reviewsRequestParamsForApply: {
      type: Object,
      required: true,
    },
  },
  emits: ['data-refresh'],
  data: function() {
    return {
      isVisibleReply: false
    }
  },
  computed: {
    ...mapState({
      company: state => state.companiesCompany.data,
    }),
    ...mapGetters({
      isAdmin: 'companiesAccessibleApi/isAdmin',
      isEnterprisePlan: 'companiesCompany/isEnterprisePlan',
      canReplyAuthority: 'companiesAccessibleApi/canReplyAuthority',
    }),
    iconReplyPath () { return iconReplyPath },
    iconReportPath () { return iconReportPath },
    canReply() {
      const { site_type: siteType, can_reply: canReply } = this.review.attributes
      switch (siteType) {
      case 'google':
        return this.canReplyAuthority && canReply && !!this.company.attributes.has_gmb_account
      // TODO: 食べログ返信UI非表示解除
      // case 'tabelog':
      //   return this.canReplyAuthority && canReply
      default:
        return false
      }
    },
    isVisibleOperations () {
      const { site_type: siteType, reviewer_name: reviewerName } = this.review.attributes
      switch (siteType) {
      case 'google':
        return this.canReply && !this.isVisibleReply && !!reviewerName
      // TODO: 食べログ返信UI非表示解除
      // case 'tabelog':
      //   return this.canReply && !this.isVisibleReply
      default:
        return false
      }
    },
    replyStatus() {
      switch (this.review.attributes.reply_status) {
      case 'posted':
        return 1
      case 'in_progress':
        return 3
      case 'error':
      case 'rejected':
        return 2
      default:
        return ''
      }
    },
    replyStatusText() {
      switch (this.review.attributes.reply_status) {
      case 'posted':
        return this.$t('pages.CompaniesReviews.replyStatus.posted')
      case 'in_progress':
        return this.$t('pages.CompaniesReviews.replyStatus.inProgress')
      case 'error':
      case 'rejected':
        return this.$t('pages.CompaniesReviews.replyStatus.error')
      default:
        return ''
      }
    },
    approvalText() {
      return this.$t(`models.approvalStatus.${this.review.attributes.reply_approval_status}`)
    },
    approvalStatus() {
      if (this.review.attributes.reply_approval_status === 'requesting') {
        return 1
      } else if (this.review.attributes.reply_approval_status === 'rejected') {
        return 2
      }
      return ''
    },
    lastEditedAtTime() {
      return moment(this.review.attributes.reply_approval_latest_edited_at).format(this.$t('format.datetime.default'))
    },
    approvalLatestStatusMessage() {
      return this.$t('models.approvalLatestStatus.' + this.review.attributes.reply_approval_status, { editor: this.review.attributes.reply_approval_latest_edited_user_name })
    },
    showApprovalFlowData() {
      return this.replyApprovalFlow && this.review.attributes.reply_approval_status && this.review.attributes.reply_approval_status !== 'approved'
    },
    showReviewImages() {
      return this.review.attributes.photos.length > 0 && this.isEnterprisePlan
    },
    locale() {
      if (this.review.attributes.translation.locale !== 'others') return this.$t(`locales.${this.review.attributes.translation.locale}`)
      if (this.$te(`locales.${this.review.attributes.translation.original_locale}`)) {
        return `${this.$t('locales.others')}（${this.$t(`locales.${this.review.attributes.translation.original_locale}`)}）`
      }
      return this.$t('locales.others')
    },
  },
  created() {
    // TODO: 食べログ返信UI非表示解除
    if (this.review.attributes.site_type !== 'google' /*&& this.review.attributes.site_type !== 'tabelog'*/) return
    if (this.review.attributes.reply_content !== '') {
      this.isVisibleReply = true
      return
    }
    if (this.replyApprovalFlow) {
      const isOpenReplyStatus = this.review.attributes.reply_approval_status === 'requesting'
        || this.review.attributes.reply_approval_status === 'rejected'
      if (isOpenReplyStatus) {
        this.isVisibleReply = true
      }
    }
  },
  methods: {
    openReply () {
      this.isVisibleReply = true
    },
    closeReply () {
      this.isVisibleReply = false
    },
    handleReport () {
      actionEvent('ReportReview')
    },
  },
}
</script>

<style lang="scss" scoped>
.translation {
  margin-top: 4px;
  &__tag {
    margin-bottom: 4px;
  }
  &--original {
    margin: 8px 0;
    padding: 16px;
    border-radius: 4px;
    background-color: #F4F4F4;
  }
}
.approvalFlowData {
  font-size: 12px;
}
.reviewImage {
  margin-top: 12px;
}
</style>
